import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Plane, MapPin, Camera, Briefcase, Heart } from 'lucide-react';

const VisitorShortStay = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Visitor and Short-Stay
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Purpose-fit applications for business visits, conferences, training, tourism, and family visits.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We prepare clear, credible visitor files that show a genuine trip and a timely return. We regularly handle the United Kingdom, United States, Canada, Schengen Europe, Australia, and the UAE. Our role is to match your purpose with the permitted activities and to organise evidence that satisfies the specific country rules. Typical response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk for evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Business Visitors</h3>
              <p className="text-gray-600 text-sm">Business visitors attending meetings, conferences, trade fairs, or short internal training</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Tourists & Family Visitors</h3>
              <p className="text-gray-600 text-sm">Tourists and family visitors seeking leisure or personal travel</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Speakers & Presenters</h3>
              <p className="text-gray-600 text-sm">Speakers or presenters at events without paid employment in country</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Medical Visitors</h3>
              <p className="text-gray-600 text-sm">Short medical treatment visitors where permitted</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Previous Refusals</h3>
              <p className="text-gray-600 text-sm">Applicants with a prior refusal who need a stronger, better documented case</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Written Route Plan</h3>
              <p className="text-gray-600 text-sm">Trip purpose confirmation, correct category, and realistic timeline</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Documented Itinerary</h3>
              <p className="text-gray-600 text-sm">Bookings and support letters that match your purpose</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Quality-Checked Application</h3>
              <p className="text-gray-600 text-sm">Complete application file with biometrics or interview scheduled</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Compliance Guidance</h3>
              <p className="text-gray-600 text-sm">Clear do and do not guidance to remain compliant during your stay</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Eligibility Basics Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Eligibility basics
          </h2>
          <p className="text-lg text-gray-600 text-center mb-8">
            Rules vary by country. The following themes are common.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Clear Purpose</h3>
                <p className="text-gray-600">A clear and credible purpose that fits permitted visitor activities</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Sufficient Funds</h3>
                <p className="text-gray-600">Funds to cover the trip without working in country</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Travel Plans</h3>
                <p className="text-gray-600">Accommodation and travel plans that make sense for the purpose and dates</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Home Country Ties</h3>
                <p className="text-gray-600">Strong ties to your home country such as job, studies, business, property, or family</p>
              </div>
            </div>
            <div className="flex items-start md:col-span-2">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Intent to Return</h3>
                <p className="text-gray-600">Willingness and ability to leave before visa expiry</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Typical Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Typical routes we cover at a high level
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">United Kingdom</h3>
              <p className="text-gray-600 text-sm">Standard Visitor for tourism, family, business meetings, short events, and some training or study in limited cases</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">United States</h3>
              <p className="text-gray-600 text-sm">B-1 for business and B-2 for tourism or family visits, ESTA for eligible nationalities</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Canada</h3>
              <p className="text-gray-600 text-sm">Temporary Resident Visa or eTA for eligible travellers</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Schengen Europe</h3>
              <p className="text-gray-600 text-sm">Short stay C visas for business or tourism, including conference travel</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Australia</h3>
              <p className="text-gray-600 text-sm">Visitor subclass for tourism or business visitor purposes</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">UAE</h3>
              <p className="text-gray-600 text-sm">Tourist and visit visas, sponsor based or hotel based depending on nationality</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            If your destination is not listed, ask during intake. We can coordinate additional countries through partners.
          </p>
        </div>
      </motion.section>

      {/* What you get from us Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What you get from us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Purpose Mapping and Plan</h3>
              <p className="text-gray-600">Confirm what is permitted for your category and map your trip to a realistic itinerary</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Document Pack and Forms</h3>
              <p className="text-gray-600">Tailored checklist, templates for purpose letters, and completed application forms</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Submission and Follow Through</h3>
              <p className="text-gray-600">Online or consular filing, appointment booking, and interview coaching where required</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">After Decision</h3>
              <p className="text-gray-600">Entry rules, permitted activity guidance, and stay reminders</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* End-to-end process Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            End-to-end process
          </h2>
          <div className="grid md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                1
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Intake and Purpose Confirmation</h3>
              <p className="text-gray-600 text-sm">Review invitations, conference tickets, meeting lists, or travel plans</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                2
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Itinerary and Evidence Build</h3>
              <p className="text-gray-600 text-sm">Structure daily plan with proof of funding, bookings, and ties to home</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                3
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Forms and Quality Checks</h3>
              <p className="text-gray-600 text-sm">Complete forms, reconcile names and dates, ensure coherent story</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                4
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Submission and Appointment</h3>
              <p className="text-gray-600 text-sm">Submit, pay fees, schedule biometrics or interview, and prepare for questions</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                5
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Decision and Trip Support</h3>
              <p className="text-gray-600 text-sm">Provide border entry tips, permitted activity guidance, and stay reminders</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Timelines Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Timelines and service levels
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Evidence Pack and Forms</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">3-10</span>
                <span className="text-gray-600 ml-2">business days</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Depending on readiness and seasonal demand</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-blue-200">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Government Processing</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">Varies</span>
                <span className="text-gray-600 ml-2">by country</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Location and peak seasons affect timing</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Appointment Availability</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">Book</span>
                <span className="text-gray-600 ml-2">early</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Conference seasons and holidays can be tight</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Communication</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">24-72</span>
                <span className="text-gray-600 ml-2">hours</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Response time, faster during booking windows</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Documents Checklist Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Documents checklist (typical)
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Identity and Travel</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Passport and previous visas</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Flight plan or draft bookings and accommodation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Travel insurance where recommended or required</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Purpose Evidence</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">For business: employer letter, invitation letters, conference registration</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">For tourism: itinerary, family invitation, event tickets</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">For training: host letter describing content and schedule</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">For medical: physician letter, appointment confirmation</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Funds and Ties</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Bank statements in acceptable formats and time periods</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Employer letters, pay slips, business registration</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Property deeds or tenancy, family records</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Additional Requirements</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Meeting schedules and proof of company relationship</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Tour bookings and detailed itineraries</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Cost estimates and funding proof for medical visits</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We confirm a tailored list during strategy because each country has specific formats and timing rules.
          </p>
        </div>
      </motion.section>

      {/* Dependents and Family Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            Dependents and family
          </h2>
          <div className="bg-blue-50 p-8 rounded-lg">
            <p className="text-lg text-gray-600 mb-6">
              Family members can often travel with you on the same category or on an appropriate family visitor category.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Household Planning</h3>
                <p className="text-gray-600 text-sm">We plan the household case together and confirm funds for the group</p>
              </div>
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Travel Alignment</h3>
                <p className="text-gray-600 text-sm">Align travel dates and prepare consent letters for minors</p>
              </div>
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Minor Documentation</h3>
                <p className="text-gray-600 text-sm">Travel permissions and school letters where required</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What is Allowed and Not Allowed Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What is allowed and not allowed
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-green-700">Generally Allowed for Business Visitors</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600">Meetings, negotiations, and conference attendance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600">Short internal training where no local employment is created</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600">Speaking at events where payment is limited or paid from outside the country</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-red-700">Generally Not Allowed</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-5 h-5 bg-red-500 rounded-full mr-3 mt-1 flex-shrink-0 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">×</span>
                  </div>
                  <span className="text-gray-600">Taking up local employment or billable work to a local client</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 bg-red-500 rounded-full mr-3 mt-1 flex-shrink-0 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">×</span>
                  </div>
                  <span className="text-gray-600">Managing projects on site that go beyond a short visit</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 bg-red-500 rounded-full mr-3 mt-1 flex-shrink-0 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">×</span>
                  </div>
                  <span className="text-gray-600">Enrolling in full time study outside limited short course allowances</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We confirm exact rules for your destination during strategy.
          </p>
        </div>
      </motion.section>

      {/* Fees Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            Fees and what is not included
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm mb-8">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Our Stage-Based Fees Include</h3>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Strategy and purpose mapping</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Document pack and forms</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Submission and follow through</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Optional travel support</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Not Included</h3>
            <p className="text-gray-600 text-sm">Government or consular fees, biometrics, medicals, travel insurance, courier or shipping, conference fees, and any lawyer fees where counsel must be engaged.</p>
          </div>
        </div>
      </motion.section>

      {/* Risks Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Risks and how we reduce them
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Unclear Purpose</h3>
              <p className="text-gray-600 mb-4">Mixed signals about the true reason for travel.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We align the file to one clear purpose and remove mixed signals.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Weak Funding Evidence</h3>
              <p className="text-gray-600 mb-4">Insufficient or inappropriate financial documentation.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We verify account types, balances, and ownership and ensure the format matches country rules.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Itinerary Gaps</h3>
              <p className="text-gray-600 mb-4">Inconsistent or incomplete travel plans.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We build a day by day plan that is consistent with bookings and meeting letters.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">One Way Travel Plans</h3>
              <p className="text-gray-600 mb-4">Open-ended or unclear return arrangements.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We recommend return reservations or a credible onward plan.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Previous Refusals or Overstays</h3>
              <p className="text-gray-600 mb-4">Past immigration issues affecting credibility.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We prepare a history summary, disclose properly, and rebuild credibility with stronger evidence.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Border Questions</h3>
              <p className="text-gray-600 mb-4">Unprepared responses at port of entry.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We supply a short arrival pack so you can answer confidently.</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* After Approval Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            After approval
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-left">
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                1
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Check Visa Details</h3>
              <p className="text-gray-600 text-sm">Check visa validity dates and number of entries</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                2
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Confirm Stay Limits</h3>
              <p className="text-gray-600 text-sm">Confirm stay limits and any rolling period rules</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                3
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Carry Documentation</h3>
              <p className="text-gray-600 text-sm">Carry printed invitations, schedules, and proof of funds for border checks</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                4
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Stay Compliant</h3>
              <p className="text-gray-600 text-sm">Do not overstay and do not perform work outside visitor rules</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What We Need Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we need to start
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Initial Information</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Travel purpose and target dates</li>
                  <li>• Invitations, conference confirmations, or meeting schedules if available</li>
                  <li>• Employer letter contact and company details for business trips</li>
                  <li>• Funding plan and current bank evidence</li>
                  <li>• Immigration history including refusals or overstays</li>
                  <li>• Family member details if travelling together</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Next Steps</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Schedule purpose confirmation and route planning</li>
                  <li>• Receive tailored document checklist</li>
                  <li>• Begin itinerary and evidence building</li>
                  <li>• Complete application forms and quality checks</li>
                  <li>• Submit application and book appointments</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Service specific FAQs
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Do I need to book flights and hotels before applying?</h3>
              <p className="text-gray-600">Often you can provide draft bookings or a refundable plan. Some consulates prefer confirmed reservations. We advise the safest approach for your country and season.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can I attend paid events or receive honoraria?</h3>
              <p className="text-gray-600">Some countries allow small honoraria or expenses for speaking. Others do not. We confirm the rule and structure payments accordingly.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can I do hands on work for a local client on a business visitor visa?</h3>
              <p className="text-gray-600">Usually no. Business visitor status is for meetings, negotiations, or short training, not productive work for a local client. We will confirm permitted activities for your itinerary.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">How much money do I need to show?</h3>
              <p className="text-gray-600">Enough to cover flights, accommodation, daily costs, and contingencies for the length of stay. Some countries publish indicative amounts. We confirm the figure and the acceptable account formats.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">What if my passport is expiring soon?</h3>
              <p className="text-gray-600">Many countries require several months of validity beyond the trip. Renew before applying if you are close to the limit.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can I extend my stay after arrival?</h3>
              <p className="text-gray-600">Sometimes yes, often no. Extensions are narrow and must be applied for before expiry. We confirm your options before you travel.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">What if my application is refused?</h3>
              <p className="text-gray-600">We provide a written analysis, identify evidence gaps, and advise on refile, administrative review, or appeal with licensed counsel where required. We only recommend a path that is credible.</p>
            </div>
          </div>
        </div>
      </motion.section>

    </div>
  );
};

export default VisitorShortStay;
